import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { adminPasswordLogin, adminVerifyMfa, adminResendEmailOtp, warmUpApi } from '../services/adminAuthApi';
import { Mail, Eye, EyeOff, LogIn, AlertCircle, Shield, CheckCircle } from 'lucide-react';
import './Admin.css';
import { COMPANY_LOGO_SRC } from '../constants/branding';

/**
 * Dedicated administrator sign-in — layout and styling match the admin dashboard (Jet Cargo Admin theme).
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [mfaStep, setMfaStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaResendBusy, setMfaResendBusy] = useState(false);

  useEffect(() => {
    if (location.state?.user?.isLoggedIn && location.state.user.role === 'admin') {
      login(location.state.user);
    }
  }, [location.state, login]);

  useEffect(() => {
    if (user?.role === 'admin' && user?.token && !user?.requiresMfaSetup) {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  // Wake the API early (free-tier servers sleep after inactivity) so the first login is fast.
  useEffect(() => {
    warmUpApi();
  }, []);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const { ok, data } = await adminPasswordLogin(email.trim(), password);
      if (!ok) {
        setError(data.message || 'Invalid credentials.');
        return;
      }
      if (data.mfaRequired && data.tempToken) {
        setMfaStep(true);
        setTempToken(data.tempToken);
        setMaskedEmail(data.maskedEmail || '');
        setMfaCode('');
        setInfo(data.message || '');
        return;
      }
      if (data.data?.token && data.data?.user) {
        const u = data.data.user;
        const next = {
          loginId: u.email,
          email: u.email,
          name: u.name,
          loginType: 'email',
          isLoggedIn: true,
          role: 'admin',
          token: data.data.token,
          requiresMfaSetup: Boolean(data.data.requiresMfaSetup),
        };
        login(next);
        navigate('/admin', { replace: true, state: { user: next } });
        return;
      }
      setError(data.message || 'Unexpected response.');
    } catch {
      setError('The server is taking longer than usual to respond. It may be waking up — please wait a few seconds and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    const code = mfaCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const { ok, data } = await adminVerifyMfa(tempToken, code);
      if (!ok || !data.data?.token) {
        setError(data.message || 'Invalid code.');
        return;
      }
      const u = data.data.user;
      const next = {
        loginId: u.email,
        email: u.email,
        name: u.name,
        loginType: 'email',
        isLoggedIn: true,
        role: 'admin',
        token: data.data.token,
        requiresMfaSetup: false,
      };
      login(next);
      setMfaStep(false);
      navigate('/admin', { replace: true, state: { user: next } });
    } catch {
      setError('Verification failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!tempToken) return;
    setMfaResendBusy(true);
    setError('');
    setInfo('');
    try {
      const { ok, data } = await adminResendEmailOtp(tempToken);
      if (!ok) {
        setError(data.message || 'Could not resend.');
        return;
      }
      setInfo(data.message || 'If email is configured, a new code was sent.');
    } catch {
      setError('Resend failed.');
    } finally {
      setMfaResendBusy(false);
    }
  };

  return (
    <div className="jet-cargo-admin jet-admin-login-root">
      <aside className="jet-admin-login-sidebar" aria-label="Brand">
        <div className="jet-admin-login-brand">
          <img
            src={COMPANY_LOGO_SRC}
            alt="Jet Shipping Cargo"
            className="jet-sidebar-brand__logo"
            decoding="async"
          />
          <span className="jet-admin-login-brand-text">JET SHIPPING CARGO</span>
        </div>
        <p className="jet-admin-login-sidebar-tag">
          Administrator dashboard — sign in with your work email and password. Two-step verification may be required.
        </p>
        <div className="jet-admin-login-sidebar-footer">
          Same look as the control panel after you sign in: royal blue sidebar and light workspace.
        </div>
      </aside>

      <div className="jet-admin-login-main">
        <div className="jet-admin-login-panel">
          <Link to="/" className="jet-admin-login-home-link">
            ← Back to home
          </Link>
          <div className="jet-admin-login-panel-head">
            <h1>{mfaStep ? 'Two-step verification' : 'Sign in to admin'}</h1>
            <p>
              {mfaStep
                ? `Enter the code from your authenticator app or email (${maskedEmail || 'your inbox'}).`
                : 'Jet Shipping Cargo — staff access only.'}
            </p>
          </div>

          <div className="jet-admin-login-panel-body">
            {error && (
              <div className="jet-admin-login-alert jet-admin-login-alert--err" role="alert">
                <AlertCircle className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
                {error}
              </div>
            )}
            {info && !error && (
              <div className="jet-admin-login-alert jet-admin-login-alert--ok">
                <CheckCircle className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
                {info}
              </div>
            )}

            {!mfaStep ? (
              <form onSubmit={handlePasswordLogin}>
                <div className="jet-admin-login-field">
                  <label htmlFor="admin-login-email">Work email</label>
                  <div className="jet-admin-login-input-wrap">
                    <Mail className="w-4 h-4 jet-admin-login-input-icon" aria-hidden />
                    <input
                      id="admin-login-email"
                      type="email"
                      autoComplete="username"
                      className="jet-admin-login-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div className="jet-admin-login-field">
                  <label htmlFor="admin-login-password">Password</label>
                  <div className="jet-admin-login-input-wrap">
                    <input
                      id="admin-login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className="jet-admin-login-input jet-admin-login-input--pwd"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="jet-admin-login-eye"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="jet-btn jet-btn-primary jet-admin-login-submit"
                >
                  <LogIn className="w-4 h-4" />
                  {isSubmitting ? 'Signing in…' : 'Continue'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleMfaSubmit}>
                <div className="jet-admin-login-mfa-row">
                  <Shield className="w-8 h-8" aria-hidden />
                  <span>6-digit verification code</span>
                </div>
                <div className="jet-admin-login-field">
                  <input
                    id="admin-mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label="6-digit verification code"
                    className="jet-admin-login-input jet-admin-login-input--otp"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                  />
                </div>
                <div className="jet-admin-login-actions-stack">
                  <button
                    type="button"
                    className="jet-btn jet-btn-secondary"
                    onClick={handleResendOtp}
                    disabled={mfaResendBusy}
                  >
                    {mfaResendBusy ? 'Sending…' : 'Resend email code'}
                  </button>
                  <button
                    type="button"
                    className="jet-btn jet-btn-ghost"
                    onClick={() => {
                      setMfaStep(false);
                      setTempToken('');
                      setMfaCode('');
                      setError('');
                      setInfo('');
                    }}
                  >
                    ← Back to password
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="jet-btn jet-btn-primary jet-admin-login-submit"
                >
                  {isSubmitting ? 'Verifying…' : 'Verify and open dashboard'}
                </button>
              </form>
            )}

            <div className="jet-admin-login-footer-link">
              Customer account?{' '}
              <Link to="/login">Go to customer sign-in</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
